import React, { Component } from 'react';
import classNames from 'classnames';
import autobind from 'autobind-decorator';
import ga from 'universal-ga';
import smoothscroll from 'smoothscroll-polyfill';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import { StaticContents } from 'components/ServerSideComponents.jsx';
import Footer from '../components/footer';
import HeroUnit from '../components/hero';
import EntryList from '../components/entry-list';
import Navigation from '../components/navigation';
import { arrow, rotate } from '../components/images';
import urlName from '../url-name';
import { replaceState } from '../components/helpers';

import animations from '../scss/components/animations.scss';
import styles from '../scss/index.scss';

/*
 * This is the brain of the application. It exposes
 * interfaces to and and accepts state changes from
 * children for use by other children. e.g. big daddy.
 *
 * @prop data - the top 100 data
 * @prop template - various static fields that control
 *   sorting & filtering.
 */
export default class Index extends Component {
	state = {
		filter: this.getFilterFromUrl(),
		focusedElementID: null,
		priorityElementID: null,
		showScrollToTop: false
	};

	getFilterFromUrl() {
		const filter = this.props.template.sectors.find(sector => window.location.pathname.includes(sector.toLowerCase()))
			? window.location.pathname.split('/').filter(Boolean).reverse()[0]
			: null;
		return filter ? `${filter.charAt(0).toUpperCase()}${filter.slice(1)}` : filter;
	}

	targetBlankLinks() {
		let links = document.links;

		for (let i = 0, linksLength = links.length; i < linksLength; i++) {
			if (links[i].hostname !== window.location.hostname) {
				links[i].target = '_blank';
			}
		}
	}

	addChartbeat() {
		let script;
		let _sf_async_config = window._sf_async_config || {};
		let currentBlog = {};
		let utils = {};
		let reportedSections = currentBlog.canonicalHost;
		let kinjaMeta = utils.kinjaMeta || {};
		let postMeta = kinjaMeta.post || {};
		let tags = kinjaMeta.tags || '';
		let storyType = postMeta.storyType || {};
		let storyTypeName = storyType.canonical || '';
		let scriptUrl = '//static.chartbeat.com/js/chartbeat.js';

		script = this.createScriptTag(scriptUrl);

		if (storyTypeName) {
			reportedSections += ',' + storyTypeName;
		}
		if (tags.length > 0) {
			reportedSections += ',' + tags;
		}

		_sf_async_config.uid = 3012;
		_sf_async_config.domain = 'www.theroot.com'; // parselyBlog.reportedHost();
		_sf_async_config.useCanonical = true;
		_sf_async_config.sections = reportedSections;
		_sf_async_config.authors = '';

		window._sf_async_config = _sf_async_config;
		window._sf_endpt = (new Date()).getTime();

		this.insertTracker(script);
	}

	addComscore() {
		const src = 'http://b.scorecardresearch.com/beacon.js';
		const script = this.createScriptTag(src);
		const _comscore = window._comscore || [];

		_comscore.push({
			c1: '2',
			c2: '6770184'
		});

		window._comscore = _comscore;
		this.insertTracker(script);
	}

	createTrackerImage(src) {
		let tracker = new Image(1, 1);
		tracker.src = src;
		this.insertTracker(tracker);
	}

	insertTracker(markup) {
		document.getElementById('trackers').appendChild(markup);
	}

	createScriptTag(src) {
		const script = document.createElement('script');

		script.setAttribute('language', 'javascript');
		script.setAttribute('type', 'text/javascript');
		script.setAttribute('src', src);
		script.setAttribute('async', 'async');

		return script;
	}

	initializeTwitter() {
		window.twttr = (function (d, s, id) {
			let js;
			let fjs = d.getElementsByTagName(s)[0];
			let t = window.twttr || {};

			if (d.getElementById(id)) {return t;}
			js = d.createElement(s);
			js.id = id;
			js.src = 'https://platform.twitter.com/widgets.js';
			fjs.parentNode.insertBefore(js, fjs);

			t._e = [];
			t.ready = function (f) {
				t._e.push(f);
			};

			return t;
		}(document, 'script', 'twitter-wjs'));
	}

	/*
	 * Runs the priority element popout via hash param
	 * on initial load.
	 */
	componentWillMount() {
		/* Kinja UA ID */
		ga.initialize('UA-142218-33');
		ga.create('UA-78232585-1', { name: 'theroot' });

		smoothscroll.polyfill();
		this.setPriorityElement();
	}

	logPageView() {
		ga.set('dimension80', '2018-09-18'); // Content Created Date
		ga.set('dimension82', 'The Root 100 - The Most Influential African Americans In 2019'); // Title
		ga.set('dimension101', 'theroot'); // Website
		ga.set('dimension83', 'interactive'); // Content Type
		ga.set('dimension109', 'website');
		ga.set('page', '/root-100-2019');
		ga.pageview('/root-100-2019');

		ga.name('theroot').set('dimension80', '2018-09-18'); // Content Created Date
		ga.name('theroot').set('dimension82', 'The Root 100 - The Most Influential African Americans In 2019'); // Title
		ga.name('theroot').set('dimension101', 'theroot'); // Website
		ga.name('theroot').set('dimension83', 'interactive'); // Content Type
		ga.name('theroot').set('dimension109', 'website');
		ga.name('theroot').set('page', '/root-100-2019');
		ga.name('theroot').pageview('/root-100-2019');
	}

	componentDidMount() {
		this.logPageView();

		this.initializeTwitter();

		this.addChartbeat();
		this.addComscore();

		window.addEventListener('scroll', this.handleScroll);
	}

	componentWillUnmount() {
		window.removeEventListener('scroll', this.handleScroll);
	}

	@autobind
	handleScroll(e) {
		const { scrollTop } = e.target.scrollingElement;
		const listElement = document.getElementById('list');

		this.setState({
			showScrollToTop: scrollTop > listElement.offsetTop
		});
	}

	@autobind
	scrollIntoView(id, name) {
		const element = document.getElementById(id);

		if (name) {
			ga.event('Interactives: The Root 100 - 2019', 'Menu: Entry Click', {eventLabel: name});
		}

		window.scroll({ top: element.offsetTop, left: 0, behavior: 'smooth' });
	}

	@autobind
	focusElement(id) {
		this.setState({
			focusedElementID: id
		});
	}

	/*
	 * Exposes filter control to navigation so changes can
	 * be applied globally
	 *
	 * @param newFilter - the new filter to apply to the list
	 * @param event - the event to prevent from happening, e.g. click
	 */
	@autobind
	updateFilter(newFilter, e) {
		if (e) {
			e.preventDefault();
		}

		const { sectors } = this.props.template;
		const { filter } = this.state;
		const resetFilter = newFilter === filter || newFilter === undefined;

		if (sectors.includes(newFilter) || resetFilter) {
			ga.event('Interactives: The Root 100 - 2019', 'Menu: Filter click', {eventLabel: newFilter || 'All'});

			this.resetPriorityElement();

			this.setState({
				filter: resetFilter ? '' : newFilter
			}, () => {
				replaceState(newFilter ? `/${urlName(newFilter)}/` : '/');
			});
		}

		this.scrollIntoView('top');
	}

	/*
	 * Exposes sidebar control to navigation so changes can
	 * be applied globally
	 *
	 * @param event - the event to prevent from happening, e.g. click
	 */
	@autobind
	toggleSidebar(e) {
		if (e) {
			e.preventDefault();
		}

		const { sidebarOpen } = this.state;

		ga.event('Interactives: The Root 100 - 2019', 'Header: Menu click', {eventLabel: sidebarOpen ? 'Close' : 'Open'});

		this.setState({
			sidebarOpen: !sidebarOpen
		});
	}

	/*
	 * This is just an event for logo click tracking
	 */
	@autobind
	logoClick() {
		ga.event('Interactives: The Root 100 - 2019', 'Header: Logo click', {eventLabel: 'http://www.theroot.com'});
	}

	/*
	 * This allows a single element to be given priority above
	 * the rest of the list. Useful for hyperlinking & sharing.
	 * The elements below it will render normally in default order.
	 */
	@autobind
	setPriorityElement(override) {
		// remove the #_ga part from the url
		if (window.location.hash && window.location.hash.match(/_ga/) && typeof window.history.replaceState === 'function') {
			window.history.replaceState({}, window.title, window.location.href.split('&')[0]);
		}
		const { pathname } = window.location;
		const elements = this.props.data.items;
		const paramMatches = pathname.match(/\/([^\/]*)\/?$/);
		const param = paramMatches.length > 0 && paramMatches[1];

		if (override || param) {
			const priorityElement = elements.find((element) => {
				const { name } = element.fields;

				if (override) {
					return override === name;
				}

				const sanitizedName = urlName(name);

				return sanitizedName === param;
			});

			if (priorityElement) {
				this.setState({ priorityElementID: priorityElement.sys.id });
			}
		}
	}

	/*
	 * Removes the priority element from the top of the list and
	 * removes the hash from the URL
	 */
	@autobind
	resetPriorityElement() {
		this.setState({
			priorityElementID: null
		});

		window.location.hash = '';
	}

	@autobind
	resetAndScrollIntoView(id, name) {
		if (this.state.filter) {
			this.updateFilter();
		}

		this.setPriorityElement(name);
		this.focusElement(id);

		setTimeout(() => {
			this.scrollIntoView(id, name);
			replaceState(`/${urlName(name)}/`);
		}, 100);
	}

	renderResetButton() {
		const { filter } = this.state;

		if (filter) {
			return (
				<div className={styles.endOfList} onClick={() => this.resetAndScrollIntoView('top')}>
					<StaticContents>
						<div>
							<p>
								Back to All
							</p>
							{rotate}
						</div>
					</StaticContents>
				</div>
			);
		}
	}

	render() {
		const { data, twitterData } = this.props;
		const elements = data.items;
		const { fields, footerLinks, sectors } = this.props.template;
		const {
			filter,
			focusedElementID,
			priorityElementID,
			showScrollToTop,
			sidebarOpen
		} = this.state;

		const listProps = {
			elements,
			fields,
			filter,
			focusedElementID,
			focusElement: this.focusElement,
			ga,
			twitterData,
			priorityElementID,
			scrollPriorityElementInView: this.scrollPriorityElementInView
		};

		const heroProps = {
			filter,
			sectors,
			updateFilter: this.updateFilter
		};

		const navProps = {
			elements,
			filter,
			resetAndScrollIntoView: this.resetAndScrollIntoView,
			sectors,
			sidebarOpen,
			logoClick: this.logoClick,
			toggleSidebar: this.toggleSidebar,
			updateFilter: this.updateFilter
		};

		const mainClass = classNames({ [`${styles.pushRight}`]: sidebarOpen }, styles.main);
		this.targetBlankLinks();

		return (
			<div>
				<ReactCSSTransitionGroup
					transitionName={{ appear: animations.appear, appearActive: animations.appearActive }}
					transitionAppear={true}
					transitionAppearTimeout={500}
					transitionLeaveTimeout={500}
					transitionEnterTimeout={500}>
					<Navigation {...navProps} />
					<div className={mainClass}>
						<HeroUnit
							{...heroProps}
							resetAndScrollIntoView={this.resetAndScrollIntoView}
						/>
						<EntryList {...listProps} />
						{this.renderResetButton()}
						<Footer footerLinks={footerLinks} ga={ga} />
					</div>
					<div className={classNames({ [styles.visible]: showScrollToTop }, styles.scrollTop)} onClick={() => this.scrollIntoView('top')}>
						<StaticContents>
							<div>
								{arrow}
							</div>
						</StaticContents>
					</div>
				</ReactCSSTransitionGroup>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./theroot/root-100-2019/src/pages/index.js